// src/pages/user/UserShowcase.tsx
// Route: /user/showcases
//
// Shows all upcoming (and optionally past) showcases relevant to the
// parent's children. Each card shows the countdown, format, location,
// and which of their children are participating.

import { useState } from "react";
import {
  useGetShowcasesQuery,
  useGetShowcaseQuery,
} from "../../app/api/parentApi";
import type { ShowcaseSummary, ShowcaseDetail } from "../../app/api/parentApi";

import { ArrowRight, CalendarDays, Check, MapPin, Mic, Sparkles, Star } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "../../components/Ico";
/* ─── atoms ─────────────────────────────────────────────────────── */

function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-xl bg-[#ebeff7] ${className}`} />
  );
}

function EmptyState({
  icon,
  title,
  subtitle,
  action,
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[22px] border border-dashed border-[#cdd4e2] bg-white py-16 text-center">
      <span className="mb-3 text-4xl"><Ico icon={icon} /></span>
      <p className="text-sm font-bold text-[#16213e]">{title}</p>
      {subtitle && (
        <p className="mt-1 max-w-xs text-xs text-[#8189a0]">{subtitle}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/* ─── format helpers ─────────────────────────────────────────────── */

const FORMAT_LABELS: Record<string, string> = {
  classroom_showcase: "Classroom Showcase",
  game_demo_day: "Game Demo Day",
  innovation_showcase: "Innovation Showcase",
  portfolio_showcase: "Portfolio Showcase",
  deployed_website_showcase: "Deployed Website Showcase",
  innovation_pitch: "Innovation Pitch",
  portfolio_presentation: "Portfolio Presentation",
  fullstack_demo_day: "Full-Stack Demo Day",
  three_platform_demo: "3-Platform Demo",
  grand_showcase: "Grand Showcase",
  other: "Showcase Event",
};

const TRACK_LABELS: Record<
  string,
  { label: string; bg: string; text: string; border: string }
> = {
  foundation_builders: {
    label: "Foundation (6–9)",
    bg: "#fff4f1",
    text: "#7a3020",
    border: "#f5c5b0",
  },
  explorers: {
    label: "Explorers (9–12)",
    bg: "#f0f7ff",
    text: "#1a4a7a",
    border: "#b0d4f5",
  },
  innovators: {
    label: "Innovators (13–18)",
    bg: "#ebeff7",
    text: "#4a44b0",
    border: "#cdd4e2",
  },
  all: {
    label: "All tracks",
    bg: "#f1f3f8",
    text: "#5a6278",
    border: "#dde2ec",
  },
};

function trackStyle(track: string) {
  return TRACK_LABELS[track] ?? TRACK_LABELS.all;
}

/* ─── countdown badge ────────────────────────────────────────────── */

function CountdownBadge({ daysLeft }: { daysLeft: number }) {
  if (daysLeft <= 0)
    return (
      <span className="inline-flex items-center rounded-full bg-[#edfaf4] px-2.5 py-0.5 text-[12.5px] font-bold text-[#2a7a56]">
        Today
      </span>
    );
  if (daysLeft === 1)
    return (
      <span className="inline-flex items-center rounded-full bg-[#fff8ee] px-2.5 py-0.5 text-[12.5px] font-bold text-[#7a4a10]">
        Tomorrow
      </span>
    );
  if (daysLeft <= 7)
    return (
      <span className="inline-flex items-center rounded-full bg-[#fff8ee] px-2.5 py-0.5 text-[12.5px] font-bold text-[#7a4a10]">
        {daysLeft} days
      </span>
    );
  return (
    <span className="inline-flex items-center rounded-full bg-[#f1f3f8] px-2.5 py-0.5 text-[12.5px] font-bold text-[#5a6278]">
      {daysLeft} days away
    </span>
  );
}

/* ─── detail drawer ──────────────────────────────────────────────── */

function ShowcaseDrawer({
  showcaseId,
  onClose,
}: {
  showcaseId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useGetShowcaseQuery(showcaseId);
  const s: ShowcaseDetail | undefined = data?.data;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[480px]"
        role="dialog"
        aria-modal
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-start justify-between border-b border-[#ebeff7] px-6 py-5">
          <div className="flex-1 min-w-0 pr-3">
            {isLoading ? (
              <Skeleton className="h-7 w-56" />
            ) : (
              <>
                <h2 className="text-lg font-bold text-[#16213e] leading-tight">
                  {s?.name}
                </h2>
                <p className="mt-0.5 text-xs text-[#8189a0]">{s?.date}</p>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl text-[#8189a0] transition hover:bg-[#f1f3f8]"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 rounded-[14px]" />
              ))}
            </div>
          ) : !s ? (
            <p className="text-sm text-[#8189a0]">Showcase not found.</p>
          ) : (
            <>
              {/* Countdown hero */}
              {s.daysLeft > 0 && (
                <div className="flex items-center gap-4 rounded-[20px] bg-[#1b2d5e] px-6 py-5 text-white">
                  <div className="text-center flex-shrink-0">
                    <p className="text-4xl font-black leading-none">
                      {s.daysLeft}
                    </p>
                    <p className="text-xs font-semibold text-white/70 mt-0.5">
                      days away
                    </p>
                  </div>
                  <div>
                    <p className="font-bold text-white">{s.name}</p>
                    <p className="text-sm text-white/80 mt-0.5">{s.date}</p>
                  </div>
                </div>
              )}

              {/* Description */}
              {s.description && (
                <p className="text-sm text-[#5a6278] leading-6">
                  {s.description}
                </p>
              )}

              {/* Details grid */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    label: "Format",
                    value: FORMAT_LABELS[s.format] ?? s.format,
                  },
                  {
                    label: "Duration",
                    value: s.durationMinutes
                      ? `${s.durationMinutes} min`
                      : "TBC",
                  },
                  { label: "Quarter", value: s.quarter ?? "—" },
                  { label: "Year", value: s.academicYear ?? "—" },
                ].map((item: { label: string; value: string }) => (
                  <div
                    key={item.label}
                    className="rounded-[14px] border border-[#ebeff7] bg-[#f9fafc] px-3 py-2.5"
                  >
                    <p className="text-[11px] font-bold uppercase tracking-wider text-[#8189a0]">
                      {item.label}
                    </p>
                    <p className="mt-0.5 text-xs font-semibold text-[#16213e]">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Location */}
              {s.location && (
                <div className="flex items-start gap-2.5 rounded-[14px] border border-[#dde2ec] bg-[#f1f3f8] px-4 py-3">
                  <span className="mt-0.5"><MapPin className="h-5 w-5" aria-hidden /></span>
                  <div>
                    <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                      Location
                    </p>
                    <p className="text-sm font-semibold text-[#16213e]">
                      {s.location}
                    </p>
                  </div>
                </div>
              )}

              {/* Flags */}
              <div className="flex flex-wrap gap-2">
                {s.parentsInvited && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#edfaf4] border border-[#b6e8d0] px-2.5 py-0.5 text-[12.5px] font-semibold text-[#2a7a56]">
                    <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Parents invited
                  </span>
                )}
                {s.externalJudges && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#ebeff7] border border-[#cdd4e2] px-2.5 py-0.5 text-[12.5px] font-semibold text-[#4a44b0]">
                    <Star className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}External judges
                  </span>
                )}
              </div>

              {/* Participating children */}
              {s.participatingChildren.length > 0 && (
                <div>
                  <p className="mb-2.5 text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                    Your children presenting
                  </p>
                  <div className="space-y-2">
                    {s.participatingChildren.map(
                      (c: ShowcaseDetail["participatingChildren"][number]) => {
                        const tc = trackStyle(c.track);
                        return (
                          <div
                            key={c.id}
                            className="flex items-center gap-3 rounded-[14px] border border-[#e5e8ef] bg-white px-4 py-3"
                          >
                            <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-[#ebeff7] text-[12.5px] font-bold text-[#1b2d5e]">
                              {c.firstName.charAt(0)}
                              {c.lastName.charAt(0)}
                            </span>
                            <p className="flex-1 text-sm font-semibold text-[#16213e]">
                              {c.firstName} {c.lastName}
                            </p>
                            <span
                              className="rounded-full px-2 py-0.5 text-[11.5px] font-bold"
                              style={{
                                background: tc.bg,
                                color: tc.text,
                                border: `1px solid ${tc.border}`,
                              }}
                            >
                              {tc.label}
                            </span>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              )}

              {/* What to expect */}
              <div className="rounded-[18px] border-2 border-[#1b2d5e] p-5">
                <p className="mb-2 text-[11.5px] font-bold uppercase tracking-wider text-[#1b2d5e]">
                  What to expect
                </p>
                <ul className="space-y-1.5 text-xs text-[#3b4560] leading-5">
                  <li>
                    <Sparkles className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Your child will present a live demo of their project
                  </li>
                  <li><Sparkles className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}They will take questions from the audience</li>
                  {s.externalJudges && (
                    <li><Sparkles className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}External judges will give structured feedback</li>
                  )}
                  {s.parentsInvited && (
                    <li>
                      <Sparkles className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}You are encouraged to ask one question about the project
                    </li>
                  )}
                  <li><Sparkles className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Arrive 10–15 minutes early for a good seat</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── showcase card ──────────────────────────────────────────────── */

function ShowcaseCard({
  s,
  onClick,
}: {
  s: ShowcaseSummary;
  onClick: () => void;
}) {
  const tc = trackStyle(s.track);
  const isPast = s.daysLeft <= 0;

  return (
    <button
      onClick={onClick}
      className={[
        "group w-full rounded-[22px] border bg-white p-5 text-left shadow-[0_2px_8px_rgba(27,45,94,0.05)] transition-all hover:shadow-[0_4px_20px_rgba(27,45,94,0.12)] hover:-translate-y-0.5",
        isPast ? "border-[#ebeff7] opacity-70" : "border-[#e5e8ef]",
      ].join(" ")}
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span
              className="rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
              style={{
                background: tc.bg,
                color: tc.text,
                border: `1px solid ${tc.border}`,
              }}
            >
              {tc.label}
            </span>
            {s.hasParticipant && (
              <span className="rounded-full bg-[#edfaf4] border border-[#b6e8d0] px-2 py-0.5 text-[11.5px] font-bold text-[#2a7a56]">
                <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Your child presents
              </span>
            )}
          </div>
          <h3 className="text-sm font-bold text-[#16213e] leading-snug">
            {s.name}
          </h3>
          <p className="mt-0.5 text-xs text-[#8189a0]">
            {FORMAT_LABELS[s.format] ?? s.format}
          </p>
        </div>
        {!isPast && <CountdownBadge daysLeft={s.daysLeft} />}
      </div>

      {/* Date */}
      <div className="flex items-center gap-2 text-xs text-[#5a6278]">
        <span><CalendarDays className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></span>
        <span className="font-semibold text-[#3b4560]">{s.date}</span>
        {s.location && (
          <>
            <span className="text-[#cdd4e2]">·</span>
            <span className="truncate"><MapPin className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{s.location}</span>
          </>
        )}
      </div>

      {/* Description */}
      {s.description && (
        <p className="mt-2 text-[12.5px] text-[#8189a0] leading-5 line-clamp-2">
          {s.description}
        </p>
      )}

      {/* Footer */}
      <div className="mt-3 flex items-center justify-between pt-3 border-t border-[#f7f8fb]">
        <div className="flex flex-wrap gap-1.5">
          {s.parentsInvited && (
            <span className="text-[11.5px] font-semibold text-[#2a7a56]">
              <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Parents invited
            </span>
          )}
          {s.externalJudges && (
            <span className="text-[11.5px] font-semibold text-[#4a44b0]">
              <Star className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Judges
            </span>
          )}
        </div>
        <span className="text-[12.5px] font-semibold text-[#1b2d5e] group-hover:text-[#4a44b0] transition-colors">
          View details{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </span>
      </div>
    </button>
  );
}

/* ─── main page ──────────────────────────────────────────────────── */

export default function UserShowcase() {
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetShowcasesQuery({ all: showAll });
  const showcases: ShowcaseSummary[] = data?.data ?? [];

  const upcoming = showcases.filter((s) => s.daysLeft > 0);
  const past = showcases.filter((s) => s.daysLeft <= 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[#16213e]">Showcase Events</h1>
          <p className="mt-1 text-sm text-[#8189a0]">
            Upcoming and past showcase events for your children's tracks.
          </p>
        </div>
        <button
          onClick={() => setShowAll((v) => !v)}
          className="flex-shrink-0 rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs font-semibold text-[#5a6278] transition hover:border-[#c9cfe0] hover:text-[#16213e]"
        >
          {showAll ? "Upcoming only" : "Show all"}
        </button>
      </div>

      {/* What is a showcase */}
      <div className="rounded-[20px] bg-gradient-to-r from-[#1b2d5e] to-[#8fa1cb] p-6 text-white">
        <p className="text-[11.5px] font-bold uppercase tracking-wider text-white/60 mb-1">
          About showcases
        </p>
        <p className="text-sm font-semibold leading-6">
          Every quarter, your child presents a live project to parents, peers,
          and invited guests. Showcases are not performances — they are proof of
          growth. Come ready to ask one genuine question about what they built.
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-[22px]" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-[18px] border border-[#f5c0c0] bg-[#fff3f3] px-5 py-4 text-sm text-[#9b2a2a]">
          Failed to load showcases. Please refresh.
        </div>
      ) : showcases.length === 0 ? (
        <EmptyState
          icon={Mic}
          title="No upcoming showcases"
          subtitle="Showcase events will appear here once they are published by the academy."
        />
      ) : (
        <div className="space-y-6">
          {/* Upcoming */}
          {upcoming.length > 0 && (
            <section>
              <p className="mb-3 text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Upcoming ({upcoming.length})
              </p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {upcoming.map((s: ShowcaseSummary) => (
                  <ShowcaseCard
                    key={s.id}
                    s={s}
                    onClick={() => setSelected(s.id)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Past */}
          {showAll && past.length > 0 && (
            <section>
              <p className="mb-3 text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Past ({past.length})
              </p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {past.map((s: ShowcaseSummary) => (
                  <ShowcaseCard
                    key={s.id}
                    s={s}
                    onClick={() => setSelected(s.id)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {selected && (
        <ShowcaseDrawer
          showcaseId={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
